import React, { useState, useEffect, useContext } from 'react';
import { useTheme } from '../../contexts/ThemeContext'; // Para obter o tema atual

// Shadcn/ui components
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose, // Para o botão de fechar, se necessário
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Spinner } from '@/components/ui/Spinner'; // Nosso Spinner personalizado
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"; // Para avisos

// Icons
import { Info } from 'lucide-react'; // Exemplo, pode precisar de outros

// Remover imports do MUI
// import {
//   Button as MuiButton,
//   Dialog as MuiDialog,
//   DialogTitle as MuiDialogTitle,
//   DialogContent as MuiDialogContent,
//   DialogActions as MuiDialogActions,
//   TextField,
//   FormControl,
//   InputLabel,
//   Select as MuiSelect,
//   MenuItem,
//   RadioGroup as MuiRadioGroup,
//   FormControlLabel,
//   Radio,
//   Box,
//   Typography,
//   CircularProgress,
//   InputAdornment,
//   Paper,
//   FormLabel,
//   SelectChangeEvent
// } from '@mui/material';
// import { InfoOutlined } from '@mui/icons-material';

import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import type { Game, MarketListing } from '../../types';

export interface ListingFormProps {
  listing?: MarketListing;
  open: boolean;
  onClose: () => void;
  onSubmit: (data: Omit<MarketListing, 'id' | 'created_at' | 'updated_at' | 'user_profile' | 'game' | 'city' | 'state' | 'country'>) => void;
}

// Definindo o tipo para os dados do formulário, que é um subconjunto de MarketListing
type ListingFormData = Omit<MarketListing, 'id' | 'created_at' | 'updated_at' | 'user_profile' | 'game' | 'city' | 'state' | 'country'>;

const ListingForm: React.FC<ListingFormProps> = ({ listing, open, onClose, onSubmit }) => {
  const { user } = useAuth();
  const { theme: currentTheme } = useTheme();

  const generateInitialFormData = (): ListingFormData => ({
    user_id: user?.id || '', // Garante que user_id está presente
    game_id: '',
    listing_type: 'sell',
    price: null,
    condition: 'new',
    description: '',
    location: '',
    status: 'active', // Status padrão para novos anúncios
  });

  const [formData, setFormData] = useState<ListingFormData>(generateInitialFormData());
  // searchTerm agora também guardará o nome do jogo selecionado para exibição no input
  const [searchTerm, setSearchTerm] = useState(''); 
  const [gameSearchResults, setGameSearchResults] = useState<Partial<Game>[]>([]);
  const [searching, setSearching] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (open) {
      if (listing) {
        setFormData({
          user_id: listing.user_id, // Usar o user_id do anúncio existente
          game_id: listing.game_id,
          listing_type: listing.listing_type,
          price: listing.price,
          condition: listing.condition || 'new',
          description: listing.description || '',
          location: listing.location || '',
          status: listing.status || 'active', // Usar o status do anúncio existente
        });
        setSearchTerm(listing.game?.name || ''); // Popular searchTerm com nome do jogo
      } else {
        setFormData(generateInitialFormData()); // Resetar com user_id e status padrão
        setSearchTerm('');
      }
      setGameSearchResults([]);
      setErrors({});
    }
  }, [listing, open, user]); // Adicionar user às dependências

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };
  
  const handlePriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setFormData(prev => ({ ...prev, price: value === '' ? null : parseFloat(value) }));
  };

  const handleRadioChange = (value: string, name: keyof ListingFormData) => {
    const newListingType = value as 'sell' | 'buy' | 'trade';
    if (name === 'listing_type') {
       setFormData(prev => {
        const updatedState = { ...prev, [name]: newListingType };
        if (!listing) { // Só reseta game_id e searchTerm se for criação e mudou tipo
          updatedState.game_id = '';
          setSearchTerm(''); 
          setGameSearchResults([]);
          setErrors(currentErrors => {
            const { game_id, ...rest } = currentErrors;
            return rest;
          });
        }
        return updatedState;
      });
    } else {
       // Para outros RadioGroups futuros, se houver
       setFormData(prev => ({ ...prev, [name]: value }));
    }
  };
  
  const handleSelectChange = (value: string, name: keyof ListingFormData) => {
    // Explicitamente não permitir undefined para condition, etc., a menos que o tipo permita.
    const fieldVal = value === '' ? null : value;
    // O tipo de formData[name] pode variar (string | null | number | undefined etc)
    // por isso o 'as any' pode ser necessário se o valor não corresponder diretamente.
    // Para 'condition', o valor será uma das strings válidas ou null.
    setFormData(prev => ({ ...prev, [name]: fieldVal as ListingFormData[keyof ListingFormData] })); 
  };

  const handleGameSearch = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const term = e.target.value;
    setSearchTerm(term);
    
    if (term.length < 2) {
      setGameSearchResults([]);
      setErrors(prev => { const { game_id, ...rest } = prev; return rest; });
      return;
    }
    
    setSearching(true);
    setErrors(prev => { const { game_id, ...rest } = prev; return rest; });
    try {
      if (!user?.id) {
        setErrors(prev => ({ ...prev, game_id: 'Você precisa estar autenticado.'}));
        setGameSearchResults([]);
        return;
      }

      let query = supabase.from('games').select('id, name, image_url, year');

      if (formData.listing_type === 'sell') {
        const { data: userGamesIds, error: userGamesIdsError } = await supabase
          .from('user_games')
          .select('game_id')
          .eq('user_id', user.id)
          .eq('owned', true);

        if (userGamesIdsError) throw userGamesIdsError;
        if (!userGamesIds || userGamesIds.length === 0) {
          setErrors(prev => ({ ...prev, game_id: 'Nenhum jogo na sua coleção.' }));
          setGameSearchResults([]);
          setSearching(false); // Parar de buscar se não há jogos
          return;
        }
        const gameIds = userGamesIds.map(ug => ug.game_id);
        query = query.in('id', gameIds).ilike('name', `%${term}%`);

      } else { 
        query = query.ilike('name', `%${term}%`);
      }

      const { data: games, error: gamesError } = await query.limit(10);

      if (gamesError) throw gamesError;
      
      setGameSearchResults(games || []);
      if ((!games || games.length === 0) && term.length >=2) {
        setErrors(prev => ({ ...prev, game_id: formData.listing_type === 'sell' ? 'Nenhum jogo na sua coleção encontrado.' : 'Nenhum jogo encontrado.'}));
      }

    } catch (error: any) {
      console.error('Erro ao buscar jogos:', error);
      setErrors(prev => ({ ...prev, game_id: error.message || 'Erro ao buscar jogos.'}));
      setGameSearchResults([]);
    } finally {
      setSearching(false);
    }
  };

  const handleSelectGame = (game: Partial<Game>) => {
    setFormData(prev => ({
      ...prev,
      game_id: game.id!,
      // game_name não está mais em formData
    }));
    setGameSearchResults([]);
    setSearchTerm(game.name || ''); // Atualiza searchTerm para exibir o nome do jogo no input
    setErrors(prev => {
      const { game_id, ...rest } = prev; 
      return rest;
    });
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.game_id) newErrors.game_id = 'Selecione um jogo';
    if (formData.listing_type !== 'trade' && (formData.price === null || formData.price <= 0)) {
      newErrors.price = 'O preço deve ser maior que zero para Venda/Compra.';
    }
    if (!formData.listing_type) newErrors.listing_type = 'Selecione o tipo de anúncio.';
    if (!formData.user_id) newErrors.user_id = 'Usuário não identificado.'; // Validação adicional
    if (!formData.status) newErrors.status = 'Status do anúncio não definido.'; // Validação adicional

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      // formData já está no formato correto (ListingFormData)
      onSubmit(formData);
    }
  };
  
  const title = listing ? 'Editar Anúncio' : 'Novo Anúncio';

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className={`w-[95vw] max-w-2xl md:w-full md:max-w-md lg:max-w-lg xl:max-w-xl 2xl:max-w-2xl ${currentTheme === 'dark' ? 'bg-gray-900 text-gray-50' : 'bg-white text-gray-900'} max-h-[90vh] overflow-y-auto`}>
        <DialogHeader>
          <DialogTitle className={`${currentTheme === 'dark' ? 'text-gray-50' : 'text-gray-900'}`}>{title}</DialogTitle>
          {/* <DialogDescription>
            Preencha os detalhes abaixo.
          </DialogDescription> */}
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-6 py-4 pb-24 sm:pb-28">
          <div>
            <Label className={`text-sm font-medium ${currentTheme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>Tipo de Anúncio</Label>
            <RadioGroup
              name="listing_type"
              value={formData.listing_type}
              onValueChange={(value) => handleRadioChange(value, 'listing_type')}
              className="flex space-x-4 mt-2"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="sell" id="sell" />
                <Label htmlFor="sell" className="font-normal">Venda</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="buy" id="buy" />
                <Label htmlFor="buy" className="font-normal">Compra</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="trade" id="trade" />
                <Label htmlFor="trade" className="font-normal">Troca</Label>
              </div>
            </RadioGroup>
            <Alert className={`mt-3 text-xs ${formData.listing_type === 'trade' ? (currentTheme === 'dark' ? 'bg-yellow-800 border-yellow-700 text-yellow-300' : 'bg-yellow-100 border-yellow-300 text-yellow-700') : formData.listing_type === 'sell' ? (currentTheme === 'dark' ? 'bg-blue-800 border-blue-700 text-blue-300' : 'bg-blue-100 border-blue-300 text-blue-700') : (currentTheme === 'dark' ? 'bg-purple-800 border-purple-700 text-purple-300' : 'bg-purple-100 border-purple-300 text-purple-700')}`}>
              <Info className="h-4 w-4" />
              <AlertDescription>
                {formData.listing_type === 'sell' 
                  ? 'Ao vender, somente jogos da sua coleção podem ser listados.'
                  : formData.listing_type === 'buy'
                  ? 'Ao comprar, você pode buscar por qualquer jogo do sistema.'
                  : 'Ao trocar, você pode buscar por qualquer jogo do sistema e não é necessário definir preço.'}
              </AlertDescription>
            </Alert>
          </div>

          {!listing && (
            <div className="relative">
              <Label htmlFor="game_search" className={`block text-sm font-medium mb-1 ${currentTheme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                {formData.listing_type === 'sell' ? "Buscar jogo na sua coleção" : "Buscar jogo"}
              </Label>
              <div className="flex items-center">
                <Input
                  id="game_search"
                  type="text"
                  placeholder={formData.listing_type === 'sell' ? "Digite o nome de um jogo da sua coleção" : "Digite o nome de qualquer jogo"}
                  value={searchTerm} // Controlado por searchTerm
                  onChange={handleGameSearch}
                  className={`w-full ${currentTheme === 'dark' ? 'bg-gray-800 border-gray-700 text-gray-50' : 'bg-white border-gray-300'}`}
                  disabled={listing !== undefined} 
                />
                {searching && <Spinner className="ml-2 h-5 w-5" />}
              </div>
              {errors.game_id && <p className="text-xs text-red-500 mt-1">{errors.game_id}</p>}
              
              {gameSearchResults.length > 0 && (
                <div className={`absolute z-10 w-full mt-1 rounded-md shadow-lg max-h-60 overflow-auto ${currentTheme === 'dark' ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-300'}`}>
                  <ul className="py-1">
                    {gameSearchResults.map((game) => (
                      <li
                        key={game.id}
                        onClick={() => handleSelectGame(game)}
                        className={`px-3 py-2 cursor-pointer ${currentTheme === 'dark' ? 'hover:bg-gray-700' : 'hover:bg-gray-100'} flex items-center space-x-2`}
                      >
                        {game.image_url && <img src={game.image_url} alt={game.name} className="h-8 w-8 object-cover rounded-sm" />}
                        <span className="text-sm">{game.name} {game.year ? `(${game.year})` : ''}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
          
          {/* Campo de nome do jogo selecionado (apenas display em edição) */}
          {listing && searchTerm && (
             <div>
                <Label className={`block text-sm font-medium ${currentTheme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>Jogo Selecionado</Label>
                {/* Usar searchTerm para exibir o nome do jogo, já que formData.game_name foi removido */}
                <p className={`mt-1 p-2 rounded-md ${currentTheme === 'dark' ? 'bg-gray-800 border border-gray-700' : 'bg-gray-100 border border-gray-200'}`}>{searchTerm}</p>
            </div>
          )}

          {/* O restante dos campos (preço, condição, descrição, localização) */}
          <div>
            <Label htmlFor="price" className={`block text-sm font-medium mb-1 ${currentTheme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>Preço (R$)</Label>
            <Input 
              id="price" 
              name="price" 
              type="number" 
              placeholder="0.00"
              value={formData.price === null ? '' : String(formData.price)} 
              onChange={handlePriceChange} 
              disabled={formData.listing_type === 'trade'} // Desabilitar se for troca
              className={`w-full ${currentTheme === 'dark' ? 'bg-gray-800 border-gray-700' : 'border-gray-300'} ${formData.listing_type === 'trade' ? (currentTheme === 'dark' ? 'disabled:bg-gray-700' : 'disabled:bg-gray-200') : ''}`}
              min="0"
              step="0.01"
            />
            {errors.price && <p className="text-xs text-red-500 mt-1">{errors.price}</p>}
          </div>

          {(formData.listing_type === 'sell' || formData.listing_type === 'trade') && (
            <div>
              <Label htmlFor="condition" className={`block text-sm font-medium mb-1 ${currentTheme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>Condição</Label>
              <Select 
                name="condition" 
                value={formData.condition || ''} 
                onValueChange={(value) => handleSelectChange(value, 'condition')}
              >
                <SelectTrigger className={`w-full ${currentTheme === 'dark' ? 'bg-gray-800 border-gray-700' : 'border-gray-300'}`}>
                  <SelectValue placeholder="Selecione a condição" />
                </SelectTrigger>
                <SelectContent className={`${currentTheme === 'dark' ? 'bg-gray-800 text-gray-50 border-gray-700' : ''}`}>
                  <SelectItem value="new">Novo (Lacrado)</SelectItem>
                  <SelectItem value="like_new">Como Novo</SelectItem>
                  <SelectItem value="good">Bom</SelectItem>
                  <SelectItem value="fair">Regular</SelectItem>
                  <SelectItem value="poor">Ruim</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          <div>
            <Label htmlFor="description" className={`block text-sm font-medium mb-1 ${currentTheme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>Descrição</Label>
            <textarea 
              id="description" 
              name="description" 
              rows={3} 
              placeholder="Detalhes sobre o jogo, estado, etc."
              value={formData.description || ''} 
              onChange={handleInputChange} 
              className={`w-full p-2 rounded-md ${currentTheme === 'dark' ? 'bg-gray-800 border-gray-700 text-gray-50' : 'bg-white border-gray-300'}`}
            />
          </div>
          
          <div>
            <Label htmlFor="location" className={`block text-sm font-medium mb-1 ${currentTheme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>Localização (Geral)</Label>
            <Input 
              id="location" 
              name="location" 
              type="text" 
              placeholder="Ex: Ponto de encontro, bairro" 
              value={formData.location || ''} 
              onChange={handleInputChange} 
              className={`w-full ${currentTheme === 'dark' ? 'bg-gray-800 border-gray-700' : 'border-gray-300'}`}
            />
          </div>

          <DialogFooter className="mt-8 pt-6 border-t dark:border-gray-700 flex flex-col sm:flex-row sm:justify-end gap-2">
            <Button type="button" variant="outline" onClick={onClose} className="w-full sm:w-auto dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-700">Cancelar</Button>
            <Button type="submit" className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 text-white dark:bg-blue-500 dark:hover:bg-blue-600">
              {listing ? 'Salvar Alterações' : 'Criar Anúncio'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ListingForm; 